
import Promise from 'bluebird';
import _ from 'lodash';
import Data from 'app-core/Data';
import config from 'app-core/config';
import auth from 'src/auth';
import audioHelper from './audio-helper';

let loadedPlugins = [];
let pluginsPromise = null;

config.eventsBus.$on('clear-cache', () => {
	pluginsPromise = null;
	loadedPlugins = [];
});

export default {
	getCategorizedPlugins(force) {
		return this.getPlugins(force).then(r => {
			var categoryGroups = _.groupBy(r.filter(f => f.category), v => JSON.stringify(v.category));
			var result = [];
			for(var catItem in categoryGroups) {
				var category = JSON.parse(catItem);
				category.isGroup = true;
				result.push(category);

				var items = categoryGroups[catItem];
				var brandGroups = _.groupBy(items.filter(f => f.brand), v => JSON.stringify(v.brand));

				category.items = [];
				
				for(var brandItem in brandGroups) {
					var brand = JSON.parse(brandItem);
					brand.items = brandGroups[brandItem];
					brand.isGroup = true;
					category.items.push(brand);
				}

				category.items.push(...items.filter(f => !f.brand));
			}
			
			var withoutCategory = r.filter(f => !f.category);
			if (withoutCategory.length) {
				result.push({ id: 'other', isGroup: true, name: config.getVueApp().$tCaption('Other', 'AudioTracker'), items: withoutCategory });
			}

			return result;
		});
	},
	getPlugins(force) {
		if (!pluginsPromise || force) {
			if (__DEBUG__)
				console.debug('Get presets');
				pluginsPromise = Data.getExternalApi('Plugin/GetPlugins', '').then(r => r.Results.filter(f => f.Enabled === true).map(f => ({
					id: f.Oid,
					name: f.Name,
					presets: f.Presets && f.Presets.map(p => ({
						id: p.Oid,
						name: p.Name
					})) || [],
					category: f.Category && {
						id: f.Category.Oid,
						name: f.Category.Name,
					},
					brand:	f.Brand && {
						id: f.Brand.Oid,
						name: f.Brand.Name
					},
					isStereo: (f.IsStereo || "false").toString().toLowerCase() === "true"
				}))).then(r => {
					loadedPlugins = r.filter(f => f.isStereo);
					return r; 
				});
		}
		return pluginsPromise.then(() => loadedPlugins);
	},
	async applyPluginsVolume(buffer, volume) {
		if (volume <= 0 || volume === 1 || !buffer) return buffer;

		if (__DEBUG__)
			console.debug('Apply plugins volume', { buffer, volume });
		
		var offlineCtx = new OfflineAudioContext(buffer?.numberOfChannels,
			buffer?.duration * buffer?.sampleRate,
			buffer?.sampleRate);

		var offlineSource = offlineCtx.createBufferSource();
		offlineSource.buffer = buffer;

		const gainNode = offlineCtx.createGain();
		gainNode.gain.value = volume || 1;
		
		offlineSource.connect(gainNode);
		gainNode.connect(offlineCtx.destination);

		offlineSource.start();

		return await offlineCtx.startRendering();
	},
	async processPlugins(data, postEffects, audioContext) {
		const plugins = postEffects?.plugins;
		if (!auth.hasProAudio() || !data || !plugins || !plugins.length) return Promise.resolve(null);

		var audioBuffer = await this.applyPluginsVolume(data?.audioBuffer, postEffects?.levelIn);
		var wav = audioHelper.bufferToWave(audioBuffer, audioBuffer?.length);
		
		if (__DEBUG__)
			window.lastProcessPlugins = data;

		var readBlob = (blob) => {
			return new Promise(resolve => {
				var reader = new FileReader();
				reader.onloadend = () => {
					resolve(reader.result);
				};

				reader.readAsDataURL(blob);
				return null;
			});
		}
		
		var base64 = await readBlob(wav);

		if (__DEBUG__)
			console.debug('Process plugins: wav', { wav, base64, data, postEffects });
		
		try {
			const dataType = Data.getBase64AudioExtension(base64);
			const base64Data = base64.split(';base64,')[1];
			
			let exception = null;
			const result = await Data.postAudioApi('Audio/ProcessVst', {
				MediaExtension: `.${dataType}`,
				Base64Content: base64Data,
				Commands: plugins.map(f => ({
					PluginId: f.id,
					PresetId: f.presetId || null
				}))
			},{
				responseType: 'blob'
			})
			.catch(async (ex) => {
				const exMsg = await Data.readBinaryJSONResponse(ex?.response?.data);
				exception = exMsg;
				if (__DEBUG__)
					console.error({ ex: ex?.response?.data, exMsg, audioBuffer });
			});

			if (__DEBUG__)
				console.debug('VST result raw', { result });

			if (!result) {
				config.notify(`${config.getVueApp().$tCaption('AudioApi exception', 'AudioTracker')}: ${exception}`, 'error', 5000);
				return {
					base64,
					buffer: await this.applyPluginsVolume(audioBuffer, postEffects?.levelOut)
				}
			}

			var resultBase64 = await readBlob(result);
			var arrayBuffer = await result.arrayBuffer();

			var buffer = await new Promise(resolve => {
				audioContext.decodeAudioData(arrayBuffer, async (decodedBuffer) => {
					var audioBuffer = await this.applyPluginsVolume(decodedBuffer, postEffects?.levelOut);
					resolve(audioBuffer);
				});
			});

			if (__DEBUG__)
				console.debug('VST result', { resultBase64, buffer });

			return {
				base64: resultBase64,
				buffer
			}
		} catch(ex) {
			console.error(ex);
			return {
				base64,
				buffer: await this.applyPluginsVolume(audioBuffer, postEffects?.levelOut)
			}
		}
	}
};
